import { useRef } from 'react'
import { Link, useParams } from 'react-router-dom'
import BombTimerLegalLanguageSelector from '../components/BombTimerLegalLanguageSelector'
import PageHero from '../components/PageHero'
import PageSeo from '../components/PageSeo'
import SiteFooter from '../components/SiteFooter'
import TopNav from '../components/TopNav'
import {
  getBombTimerLegalLocale,
  getBombTimerLegalPath,
  getBombTimerLegalTranslation,
  useBombTimerLegalTranslation,
} from '../lib/bombTimerLegal'
import { buildOrganizationSchema, getAbsoluteUrl } from '../lib/seo'
import './ArticlePage.css'

const effectiveDate = '2026-09-28'
export default function BombTimerTermsAndConditionsPage() {
  const { locale: localeParam } = useParams<{ locale: string }>()
  const locale = getBombTimerLegalLocale(localeParam)
  const rootRef = useRef<HTMLDivElement>(null)
  const localizedTermsPath = getBombTimerLegalPath('terms-and-conditions', locale)
  const localizedPolicyPath = getBombTimerLegalPath('privacy-policy', locale)
  const translate = (source: string) => getBombTimerLegalTranslation(locale, source)
  useBombTimerLegalTranslation(rootRef, locale)

  return (
    <div
      key={locale}
      ref={rootRef}
      className="article-page"
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
      lang={locale}
    >
      <PageSeo
        title={translate('Terms and Conditions | Bomb Timer')}
        description={translate(
          'Read the Bomb Timer Terms and Conditions, including permitted use, purchases, subscriptions, safety, availability, and liability.',
        )}
        canonical={getAbsoluteUrl(localizedTermsPath)}
        image={getAbsoluteUrl('/images/bomb-timer-logo.png')}
        keywords={[
          'bomb timer terms and conditions',
          'bomb timer app terms',
          'terms and conditions',
        ]}
        schema={[buildOrganizationSchema()]}
      />

      <TopNav />

      <main>
        <article className="article-shell">
          <PageHero as="header" innerClassName="article-hero__inner">
            <nav className="article-breadcrumbs" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>Bomb Timer</span>
              <span>/</span>
              <span>Terms and Conditions</span>
            </nav>

            <p className="eyebrow eyebrow--blog">Bomb Timer · Legal</p>
            <h1 className="article-hero__title heading-light">Terms &amp; Conditions</h1>
            <p className="article-hero__description p-intro">
              These Terms govern your use of the Bomb Timer mobile application.
            </p>

            <div className="article-hero__meta">
              <span>Effective date</span>
              <span>{effectiveDate}</span>
            </div>

            <BombTimerLegalLanguageSelector document="terms-and-conditions" locale={locale} />
          </PageHero>

          <div className="container article-body-wrap">
            <div className="article-layout article-layout--single">
              <div className="article-content article-content--legal">
                <p className="article-intro">
                  These Terms and Conditions ("Terms") govern your use of the Bomb Timer mobile
                  application (the "Application"), operated by Hatcher trading as Contrast Shower
                  ("we", "us" or "our"), an individual/sole trader based in Queensland, Australia.
                  The Application&apos;s bundle identifier and Android package name are
                  com.bombtimer.app.
                </p>

                <section className="article-section">
                  <p>
                    By downloading or using the Application, you agree to these Terms. If you do not
                    agree, do not use the Application. If you are not old enough to enter into these
                    Terms yourself, a parent or legal guardian must review and agree to them for
                    you.
                  </p>
                </section>

                <section className="article-section">
                  <h2>1. What Bomb Timer Is</h2>
                  <p>
                    Bomb Timer is a playful countdown game with visual effects, audio and haptics.
                    It is intended for entertainment and casual timing only. It does not detect,
                    control, defuse or interact with explosives or other physical devices.
                  </p>
                  <p>
                    Do not rely on the Application for emergencies, personal safety, medical
                    purposes, cooking or industrial processes, competitive timekeeping, or any
                    activity where an inaccurate, interrupted or missed timer could cause injury,
                    damage or loss. Always obey applicable laws and remain aware of your
                    surroundings while using it.
                  </p>
                </section>

                <section className="article-section">
                  <h2>2. Licence and Acceptable Use</h2>
                  <p>
                    We grant you a personal, limited, revocable, non-exclusive and non-transferable
                    licence to use the Application for lawful personal use, subject to these Terms
                    and the rules of the app store from which you obtained it.
                  </p>
                  <p>You must not:</p>
                  <div className="article-list-block">
                    <ul>
                      <li>
                        use the Application unlawfully, deceptively, to threaten or harass others,
                        or in a way likely to cause harm;
                      </li>
                      <li>
                        present the Application as a real explosive device or use its sounds or
                        visuals to create panic or mislead others;
                      </li>
                      <li>
                        copy, modify, distribute, sell or create derivative works from the
                        Application except where applicable law permits;
                      </li>
                      <li>
                        reverse-engineer or attempt to extract source code except where that
                        restriction is prohibited by law;
                      </li>
                      <li>
                        bypass usage limits, purchase checks, entitlement checks or security
                        features; or
                      </li>
                      <li>
                        interfere with the Application, RevenueCat, an app store or related systems.
                      </li>
                    </ul>
                  </div>
                  <p>
                    The Application, its branding, design, content and software are owned by us or
                    our licensors. These Terms do not transfer any ownership rights to you.
                  </p>
                </section>

                <section className="article-section">
                  <h2>3. Free Access and Premium Purchases</h2>
                  <h3>3.1 Free Access</h3>
                  <p>
                    Bomb Timer currently provides up to two free timer uses per local calendar day.
                    We may change the free allowance or the features included in free and premium
                    access in a future version, subject to applicable law and app-store
                    requirements.
                  </p>

                  <h3>3.2 Premium Options</h3>
                  <p>
                    Premium access may be offered as a monthly auto-renewing subscription, yearly
                    auto-renewing subscription, or one-time lifetime in-app purchase. The options
                    available to you, current price, currency, billing period and any introductory
                    offer are displayed in the Application and confirmed by the applicable app store
                    before purchase. "Lifetime" means access for the supported lifetime of the
                    Application, not the purchaser&apos;s lifetime, and does not guarantee that the
                    Application or every feature will remain available indefinitely.
                  </p>

                  <h3>3.3 Billing and Renewal</h3>
                  <p>
                    Apple or Google processes your payment through your app-store account.
                    RevenueCat is used to validate purchases and manage premium entitlements. We do
                    not directly collect your full payment-card or bank-account details.
                  </p>
                  <p>
                    Monthly and yearly subscriptions renew automatically unless you cancel them
                    using your app-store subscription settings before renewal. Cancellation stops
                    future renewals and normally leaves premium access active until the end of the
                    paid billing period. Deleting the Application does not cancel a subscription.
                  </p>

                  <h3>3.4 Restores, Refunds and Price Changes</h3>
                  <p>
                    You can use the Application&apos;s restore function or Customer Center to
                    restore or manage eligible purchases. Purchase availability and restoration
                    remain subject to the app store&apos;s rules and the account used for the
                    original purchase.
                  </p>
                  <p>
                    Refund requests are handled under Apple&apos;s or Google&apos;s applicable
                    policies and applicable consumer law. We do not process app-store payments
                    directly. Prices may vary by country because of local pricing, taxes and
                    app-store tiers. Any subscription price change will be handled under applicable
                    law and app-store requirements.
                  </p>
                  <p>
                    Nothing in these Terms excludes, restricts or modifies a consumer guarantee,
                    right or remedy that cannot lawfully be excluded, including rights under the
                    Australian Consumer Law.
                  </p>
                </section>

                <section className="article-section">
                  <h2>4. Accounts, Connectivity and Third Parties</h2>
                  <p>
                    Bomb Timer does not currently require its own user account. Purchases are
                    associated with an anonymous RevenueCat identifier and your applicable app-store
                    account.
                  </p>
                  <p>
                    Core timer functionality is designed to operate on your device, but purchase,
                    restoration, subscription-management and entitlement-refresh features require a
                    compatible device, an internet connection and availability of RevenueCat and the
                    applicable app store. Carrier or internet charges are your responsibility.
                  </p>
                  <p>
                    Apple, Google and RevenueCat are independent service providers with their own
                    terms. Apple and Google are not responsible for providing maintenance or support
                    for the Application except to the extent required by their platform terms or
                    applicable law.
                  </p>
                </section>

                <section className="article-section">
                  <h2>5. Privacy</h2>
                  <p>
                    Our <Link to={localizedPolicyPath}>Bomb Timer Privacy Policy</Link>{' '}
                    explains how information is handled. In summary, the Application stores language
                    and free-use information locally and uses RevenueCat, Apple and Google for
                    purchases and premium entitlement management. Bomb Timer does not currently
                    include advertising or third-party analytics SDKs and does not require an
                    account.
                  </p>
                </section>

                <section className="article-section">
                  <h2>6. Availability, Updates and Changes</h2>
                  <p>
                    We may update, change, suspend or discontinue all or part of the Application. We
                    do not guarantee that it will always be available, compatible with every device
                    or operating-system version, error-free, or that a notification, sound, haptic
                    or timer will occur at an exact time. Device settings, operating-system
                    restrictions, audio routing, interruptions, battery state and other factors can
                    affect operation.
                  </p>
                  <p>
                    You are responsible for installing updates and maintaining a compatible, secure
                    device. We may suspend access where reasonably necessary to address misuse,
                    fraud, security risks, legal requirements or a breach of these Terms.
                  </p>
                </section>

                <section className="article-section">
                  <h2>7. Disclaimers and Liability</h2>
                  <p>
                    To the maximum extent permitted by law, the Application is provided "as is" and
                    "as available" without warranties beyond those that cannot legally be excluded.
                    We do not warrant uninterrupted availability, exact timer accuracy or fitness
                    for a safety-critical purpose.
                  </p>
                  <p>
                    To the maximum extent permitted by law, we are not liable for indirect,
                    incidental, special or consequential loss arising from use of or inability to
                    use the Application. Our aggregate liability arising from the Application will
                    not exceed the amount you paid for it during the 12 months before the event
                    giving rise to the claim. These limitations do not apply where liability cannot
                    lawfully be excluded or limited.
                  </p>
                </section>

                <section className="article-section">
                  <h2>8. Changes to These Terms</h2>
                  <p>
                    We may update these Terms when the Application, purchasing options or legal
                    requirements change. We will publish revised Terms on this page and update the
                    effective date. Material changes may also be communicated within the Application
                    where reasonably appropriate. If you do not agree to revised Terms, stop using
                    the Application and cancel any active subscription through your app store.
                  </p>
                </section>

                <section className="article-section">
                  <h2>9. Governing Law and Disputes</h2>
                  <p>
                    These Terms are governed by the laws applicable in Queensland, Australia,
                    without excluding any mandatory rights you have under the laws of your place of
                    residence. You submit to the non-exclusive jurisdiction of the courts of
                    Queensland, Australia. Please contact us first so we can try to resolve any
                    concern informally.
                  </p>
                </section>

                <section className="article-section">
                  <h2>10. Contact Us</h2>
                  <p>
                    For questions, support or complaints about Bomb Timer, contact Hatcher trading
                    as Contrast Shower at{' '}
                    <a href="mailto:support@contrastshower.com">support@contrastshower.com</a>.
                  </p>
                </section>
              </div>
            </div>
          </div>
        </article>
      </main>

      <SiteFooter />
    </div>
  )
}
